import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { SUPPLY_CATEGORIES, type SupplyCategory } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SearchField } from "@/components/app/patterns/search-field";
import { TableCard } from "@/components/app/patterns/table-card";
import { Button } from "@/components/ui/button";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Toggle } from "@/components/ui/toggle";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { KEEP_PLACE, optionalOneOf, optionalTrue, searchPatch, optionalSearch } from "@/lib/search";
import { AlertTriangle, Plus } from "@/lib/icons";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { orpc } from "@/lib/orpc";
import { MenusPager } from "@/routes/_authenticated/menus/-components/menus-pager";
import { usePaging } from "@/routes/_authenticated/menus/-lib/use-paging";
import { useKitchen } from "@/routes/_authenticated/menus/-lib/use-kitchen";
import { ClosetDialog } from "./-components/closet-dialog";
import { ClosetItemForm } from "./-components/closet-item-form";
import { ClosetTable } from "./-components/closet-table";
import { OnTheWay } from "./-components/on-the-way";
import { SupplyCategoryOptions } from "./-components/supply-category-options";

export const Route = createFileRoute("/_authenticated/supplies/closet")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; category?: SupplyCategory; low?: true } => ({
    q: optionalSearch(search.q),
    category: optionalOneOf(SUPPLY_CATEGORIES, search.category),
    low: optionalTrue(search.low),
  }),
  component: ClosetPage,
});

/** What is in the supply closet, counted in singles, by category, with what is low. */
function ClosetPage() {
  const { facilityId, canManage } = useKitchen();
  const { q, category: pickedCategory, low: pickedLow } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = searchPatch(navigate);
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const category: SupplyCategory | "" = pickedCategory ?? "";
  const setCategory = (next: SupplyCategory | "") => patch({ category: next || undefined });
  const low = Boolean(pickedLow);
  const setLow = (next: boolean) => patch({ low: next ? true : undefined });
  const [adding, setAdding] = useState(false);
  const paging = usePaging(100);
  const closet = useQuery({
    ...orpc.agents.supplies.closet.list.queryOptions({
      input: {
        facilityId,
        limit: paging.size,
        offset: paging.offset,
        ...(q ? { search: q } : {}),
        ...(category ? { category } : {}),
        ...(low ? { low } : {}),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || closet.isPlaceholderData;
  const filtered = Boolean(search || category || low);
  const clear = () => {
    void navigate({ search: {}, ...KEEP_PLACE });
    paging.reset();
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          className="max-w-xs sm:w-full"
          value={search}
          onChange={(next) => {
            setSearch(next);
            paging.reset();
          }}
          placeholder="Search the closet"
        />
        <OptionSelect
          aria-label="Category"
          value={category}
          className="w-48"
          onChange={(event) => {
            setCategory(event.target.value as SupplyCategory | "");
            paging.reset();
          }}
        >
          <SelectOption value="">Every category</SelectOption>
          <SupplyCategoryOptions />
        </OptionSelect>
        <Toggle
          variant="outline"
          size="sm"
          pressed={low}
          onPressedChange={(next) => {
            setLow(next);
            paging.reset();
          }}
        >
          <AlertTriangle className="size-3.5" aria-hidden />
          Low only
          {closet.data?.lowCount ? (
            <span className="text-faint tabular-nums">{String(closet.data.lowCount)}</span>
          ) : null}
        </Toggle>
        {canManage ? (
          <div className="ml-auto">
            <ClosetDialog
              open={adding}
              onOpenChange={setAdding}
              title="Add an item to the closet"
              description="Count it in the singles you take out, like rolls or bottles."
              trigger={
                <Button size="sm">
                  <Plus className="size-3.5" />
                  Add item
                </Button>
              }
            >
              <ClosetItemForm facilityId={facilityId} start={{}} onSaved={() => setAdding(false)} />
            </ClosetDialog>
          </div>
        ) : null}
      </div>

      <OnTheWay facilityId={facilityId} canManage={canManage} />

      {closet.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : closet.isError ? (
        <ReadFailed
          message="Couldn't load the closet."
          error={closet.error}
          onRetry={() => void closet.refetch()}
        />
      ) : closet.data.rows.length === 0 && searching ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : closet.data.rows.length === 0 ? (
        <EmptyState
          title={filtered ? "Nothing in the closet matches" : "The closet is empty"}
          description={
            filtered
              ? "Check the spelling, or clear the filters."
              : "Add what the closet holds, or send the Kitchen agent a photo to count it. Give an item a minimum and it is suggested for the list when it runs low."
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableCard busy={searching}>
          <ClosetTable facilityId={facilityId} canManage={canManage} items={closet.data.rows} />
          <MenusPager paging={paging} total={closet.data.total} noun={["item", "items"]} />
        </TableCard>
      )}
    </section>
  );
}
